import { Controller } from 'stimulus';
import { FocusTrap } from 'shared/assets/javascript/accessibility/focusTrap.js';
import Swiper, { A11y, Lazy, Navigation, Pagination } from 'swiper';
Swiper.use([A11y, Lazy, Navigation, Pagination]);

export default class extends Controller {
  static targets = [
    'cta',
    'close',
    'carousel',
    'carouselWrapper',
    'controls',
    'links',
    'caption'
  ];

  get closeButton() {
    return document.querySelector('.gallery-overlay__close-button');
  }

  get ctaButton() {
    return this.ctaTarget?.querySelector('.btn__cta') || null;
  }

  get galleryGridAssets() {
    return document.querySelectorAll('.-gallery-overlay-asset');
  }

  connect() {
    this.state = {
      open: false,
      video: false,
      playing: false,
      isTouch: Site.environment.touch,
      isMobile: Site.environment.isMobile,
      defaultCtaUrl: this.hasCtaTarget ? this.ctaButton?.getAttribute('href') : '',
      defaultCtaTitle:this.hasCtaTarget ?  this.ctaTarget.textContent : '',
    };
    const modal = document.querySelector('.gallery-overlay');
    const modalToggle = this.closeButton;
    this.focusTrapInstance = new FocusTrap([modalToggle, modal]);
    if (Site.environment.viewportUnits) {
      this.setupEvents();
    }
  }

  disconnect() {
    this.removeEvents();
    this.removeCarousel();
  }

  setupEvents() {
    this.galleryGridAssets.forEach(asset => {
      asset.addEventListener('click', this.open.bind(this));
    });
    this.closeButton.addEventListener('click', this.close.bind(this));
  }

  removeEvents() {
    this.galleryGridAssets.forEach(asset => {
      asset.removeEventListener('click', this.open.bind(this));
    });
    this.closeButton.removeEventListener('click', this.close.bind(this));
  }

  open(event) {
    if (event.target.href && !event.target.href.includes('#gallery')) return;
    event.preventDefault();
    document.body.classList.add('-hide-page-overflow');
    this.focusTrapInstance.set();
    this.closeButton.focus();

    if (this.state.open) {
      this.removeCarousel();
    }

    const clicked = event.target.closest('.-gallery-overlay-asset');
    const galleryItems = document.querySelector('.gallery-card') ?
      document.querySelectorAll('.gallery-card:not(.-is-hidden) .-gallery-overlay-asset') :
      document.querySelectorAll('.-gallery-overlay-asset');
    const galleryHtml = this.getGalleryHtml(galleryItems);
    const galleryIndex = [...galleryItems].indexOf(clicked);
    this.element.setAttribute('aria-hidden', 'false');
    this.carouselWrapperTarget.innerHTML = galleryHtml;
    this.state.open = true;
    if (galleryItems.length) {
      requestAnimationFrame(() => {
        this.initCarousel(galleryIndex);
        this.lazyLoadCarouselAssets();
      })
    }
  }

  close(event) {
    if (event) event.preventDefault();
    document.body.classList.remove('-hide-page-overflow');
    this.focusTrapInstance.unset();
    this.element.setAttribute('aria-hidden', 'true');
    this.element.removeAttribute('inert', '');
    this.removeCarousel();
    this.clearVideo();
    this.state.open = false;
  }

  getGalleryHtml(items) {
    const renderVideo = !this.state.isMobile && !this.state.isTouch;
    return Array.from(items).map(item => {
      const ctaUrl = item.getAttribute('data-cta-url') || '';
      const ctaTitle = item.getAttribute('data-cta-title') || '';
      let innerHtml = item.outerHTML;
      const videoUrl = item.querySelector('[data-video_url]')?.getAttribute('data-video_url');
      const assetType = item.querySelector('video') ? 'video' : (item.querySelector('.asset-holder__image--embed') ? 'embed' : 'image');
      const image = item.querySelector('img');
      const src = this.getImageSrc(image);
      const caption = image.parentElement.querySelector('.asset-holder__image--caption')?.textContent || '';
      if (assetType === 'video' && renderVideo) {
        innerHtml = item.querySelector('video').outerHTML;
        return `
          <div class='gallery-overlay__media swiper-slide' role='group'>
            <div class='gallery-overlay__video' data-caption="${caption}" data-cta-url='${ctaUrl}' data-cta-title='${ctaTitle}'>
              <div class='gallery-overlay__video-container'>
                ${innerHtml}
              </div>
            </div>
          </div>
        `;
      } else if (assetType === 'embed') {
        if (videoUrl) {
          return `
            <div class='gallery-overlay__media swiper-slide' role='group'>
              <div class='gallery-overlay__video-container' data-cta-url='${ctaUrl}' data-cta-title='${ctaTitle}'>
                <iframe class='gallery-overlay__embed' src="${videoUrl}" data-video_url="${videoUrl}" title='gallery video'>
                </iframe>
              </div>
            </div>
          `;
        }
      } else {
        if (src) {
          return `
            <div class='gallery-overlay__media swiper-slide' role='group'>
              <img class='gallery-overlay__image' src='${src}' data-caption="${caption}" data-cta-url='${ctaUrl}' data-cta-title='${ctaTitle}' />
            </div>
          `;
        }
      }
    }).join('');
  }

  getImageSrc(image) {
    if (this.state.isMobile) {
      return image.getAttribute('data-gallery') || image.getAttribute('src') || image.getAttribute('data-lazy_src') || false;
    } else {
      return image.getAttribute('data-gallery-x2000') || image.getAttribute('src') || image.getAttribute('data-lazy_src') || false;
    }
  }

  initCarousel(index) {
    this.swiper = new Swiper(this.carouselTarget, {
      loop: true,
      slidesPerView: 1,
      spaceBetween: 0,
      lazy: true,
      preloadImages: false,
      initialSlide: index,
      navigation: {
        prevEl: '.gallery-overlay__prev',
        nextEl: '.gallery-overlay__next',
      },
      a11y: {
        liveRegion: false,
        slideLabelMessage: 'Slide {{index}} of {{slidesLength}}'
      },
      on: {
        afterInit: (swiper) => {
          this.setCta(swiper);
          this.setVideo(swiper);
        },

        slideChange: (swiper) => {
          this.setCta(swiper);
          this.setCaption(swiper);
          this.setVideo(swiper);
        },

        lazyImageReady: () => {
          this.carouselTarget.trigger('scroll');
        },
      }
    });
  }

  removeCarousel() {
    this.carouselWrapperTarget.innerHTML = '';
  }

  lazyLoadCarouselAssets() {
    document.dispatchEvent(new Event('scroll'));
  }

  setVideo(swiper) {
    const slide = swiper.slides[swiper.activeIndex];
    const video = slide.querySelector('video') || false;
    const hasVideo = video.length || false;
    if (hasVideo) {
      this.state.video = video;
      this.toggleVideo();
    } else {
      this.toggleVideo();
      this.state.video = false;
    }
  }

  setCaption(swiper) {
    const slide = swiper.slides[swiper.activeIndex];
    const caption = slide.querySelector('[data-caption]')?.getAttribute('data-caption') || '';
    if (this.hasCaptionTarget) {
      this.captionTarget.textContent = caption;
    }
  }

  setCta(swiper) {
    const slide = swiper.slides[swiper.activeIndex];
    const ctaUrl = slide.querySelector('[data-cta-url]')?.getAttribute('data-cta-url') || this.state.defaultCtaUrl;
    const ctaTitle = slide.querySelector('[data-cta-title]')?.getAttribute('data-cta-title') || this.state.defaultCtaTitle;
    if (this.hasCtaTarget && !!this.ctaButton) {
      this.ctaButton.textContent = ctaTitle;
      this.ctaButton.setAttribute('href', ctaUrl);
    }
  }

  toggleVideo() {
    if (this.state.video && this.state.playing) {
      this.pauseVideo();
    } else if (this.state.video && !this.state.playing) {
      this.playVideo();
    }
  }

  playVideo() {
    if (this.state.video) {
      this.state.video.play();
    }
    this.state.playing = true;
  }

  pauseVideo() {
    if (this.state.video) {
      this.state.video.pause();
    }
    this.state.playing = false;
  }

  clearVideo() {
    this.state.video = false;
    this.state.playing = false;
  }

  setEmbed(swiper) {
    const slide = swiper.slides[swiper.activeIndex];
    const embed = slide.querySelector('iframe') || false;
    const hasEmbed = embed.length || false;
    if (hasEmbed) {
      const videoUrl = embed.getAttribute('data-video_url');
      document.querySelector('.gallery-overlay__embed').setAttribute('src', '');
      embed.setAttribute('src', videoUrl);
    } else {
      document.querySelector('.gallery-overlay__embed').setAttribute('src', '');
    }
  }
}

